import { createContext, useBoolean } from '@hover/blueprint';
import { entries } from '@hover/utilities';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { useMemoContextValue } from 'src/shared/hooks';
import { logWarning } from 'src/shared/telemetry/logger';

import type { RouteMap } from '../constants';
import { isCustomizeRoute, isFullscreenImageRoute } from '../helpers';

export interface RoutesContext {
  /**
   * React Router `Location` for previous route, if any
   */
  previousRoute?: ReturnType<typeof useLocation>;
  /**
   * Whether the previous route was a customize route
   */
  wasCustomizeRoute: boolean;
  /**
   * Whether the previous route was a fullscreen image route
   */
  wasFullscreenImageRoute: boolean;
  routes: RouteMap;
}

const [Provider, useContext] = createContext<RoutesContext | undefined>({
  strict: false,
});

const HISTORY_LENGTH = 2;

const useIsFirstRoute = () => {
  const { pathname } = useLocation();

  const [initialPathname] = useState(pathname);
  const [isFirstRoute, { off }] = useBoolean(true);

  if (pathname !== initialPathname && isFirstRoute) off();

  return isFirstRoute;
};

const useLocations = () => {
  const location = useLocation();
  const [locations, setLocations] = useState([location]);

  if (locations[0].pathname !== location.pathname) {
    setLocations(current => [location, ...current].slice(0, HISTORY_LENGTH));
  }

  return locations;
};

export interface RoutesProviderProps
  extends PropsWithChildren<{}>,
    Pick<RoutesContext, 'routes'> {}

export const RoutesProvider = ({ routes, ...props }: RoutesProviderProps) => {
  const locations = useLocations();
  const isFirstRoute = useIsFirstRoute();

  const previousLocation = locations[1];

  const { pathname: previousPathname } = previousLocation ?? {};

  const value = useMemoContextValue({
    routes,
    isFirstRoute,
    previousRoute: previousLocation,
    wasCustomizeRoute: isCustomizeRoute(previousPathname),
    wasFullscreenImageRoute: isFullscreenImageRoute(previousPathname),
  });

  return <Provider value={value} {...props} />;
};

const routeForPathname = (
  pathname: string,
  routes: RouteMap,
  method: 'load' | 'preload',
) => {
  const [key, route] =
    entries(routes).find(
      ([, { exact, path }]) => !!matchPath(pathname, { exact, path }),
    ) ?? [];

  if (!key || !route) {
    const message = `useRoutes: couldn't find a route to ${method} for '${pathname}'`;

    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.warn(`🚧 ${message}`);
    } else {
      logWarning(message);
    }

    return null;
  }

  const { component } = route;

  if (__DEV__) {
    const action = method === 'load' ? 'Loading' : 'Preloading';

    // eslint-disable-next-line no-console
    console.info(`🚚 ${action} '${key}' route for '${pathname}'`);
  }

  return component;
};

/**
 * A route helper hook that provides information about the current and previous
 * routes (since this information isn't available via React Router's `history`)
 * as well as `load` and `preload` functions for preloading route chunks.
 *
 * @example
 * ```ts
 * const history = useHistory();
 * const { preload } = useRoutes();
 *
 * const pathname = '/designer/saved';
 *
 * await load(pathname);
 * history.push(pathname);
 * ```
 */
export const useRoutes = () => {
  const { routes = {}, ...rest } = useContext() ?? {};

  const load = useCallback(
    async (pathname: string) => {
      await routeForPathname(pathname, routes, 'load')?.load();
    },
    [routes],
  );

  const preload = useCallback(
    (pathname: string) => {
      routeForPathname(pathname, routes, 'load')?.preload();
    },
    [routes],
  );

  return {
    /**
     * Whether this is the first route the user has visited since the initial
     * server-side page render
     */
    isFirstRoute: useIsFirstRoute(),
    ...rest,
    /**
     * Given a `pathname`, load the matching route's `LoadableComponent`
     * chunk.
     *
     * This is useful in cases where we need to be able to immediately render a
     * route when navigating via `history.push` for the purposes of animation.
     *
     * @params pathname a `pathname` that matches the `path` of a route.
     * This should be the same `pathname` that will be passed to `history.push`.
     */
    load,
    /**
     * Given a `pathname`, preload the matching route's `LoadableComponent`.
     * This prefetches the route in the background, if you need to wait for the
     * route to be loaded, use `load()` instead.
     *
     * @params pathname a `pathname` that matches the `path` of a route.
     */
    preload,
  };
};
